import { CircleMarker, MapContainer, Marker, Polyline, Popup, TileLayer, Tooltip, ZoomControl, useMapEvents } from "react-leaflet";

function MapInteractionBridge(props) {
  useMapEvents({
    click: function () {
      if (props.onMapBackgroundClick) props.onMapBackgroundClick();
    },
    popupopen: function () {
      if (props.onPopupChange) props.onPopupChange(true);
    },
    popupclose: function () {
      if (props.onPopupChange) props.onPopupChange(false);
    }
  });

  return null;
}

export default function WorldMap(props) {
  const {
    mapCenter,
    mapRef,
    createMode,
    airports,
    getAirport,
    draftOrigin,
    draftDestination,
    greatCirclePositions,
    routes,
    fleet,
    getPlane,
    routeProgress,
    planePoint,
    planeIcon,
    airportCode,
    money,
    planeCondition,
    visibleAirports,
    draftRoute,
    onAirportPick,
    airportTypeLabels,
    airportMarkerRadius,
    onMapBackgroundClick,
    onPopupChange
  } = props;

  return (
    <MapContainer center={mapCenter} zoom={4} minZoom={2} zoomControl={false} worldCopyJump={true} className={createMode ? "map picking" : "map"} ref={mapRef}>
      <MapInteractionBridge onMapBackgroundClick={onMapBackgroundClick} onPopupChange={onPopupChange} />
      <ZoomControl position="bottomright" />
      <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

      {draftOrigin && draftDestination && (
        <Polyline positions={greatCirclePositions(draftOrigin, draftDestination, 80)} className="draftLine" pathOptions={{ color: "#ffffff", weight: 2, opacity: 0.72 }} />
      )}

      {routes.map(function (route) {
        const routeOrigin = getAirport(airports, route.originId);
        const routeDestination = getAirport(airports, route.destinationId);
        if (!routeOrigin || !routeDestination) return null;
        return <Polyline key={route.id} positions={greatCirclePositions(routeOrigin, routeDestination, 96)} className="routeLine" pathOptions={{ color: "#33e1ff", weight: 3, opacity: 0.9 }} />;
      })}

      {routes.map(function (route) {
        const routeOrigin = getAirport(airports, route.originId);
        const routeDestination = getAirport(airports, route.destinationId);
        const plane = getPlane(fleet, route.planeId);
        if (!routeOrigin || !routeDestination || !plane) return null;
        const state = typeof routeProgress[route.id] === "object" ? routeProgress[route.id] : { progress: Number(routeProgress[route.id] || 0), dir: "out" };
        const point = planePoint(routeOrigin, routeDestination, state);
        const manifest = state.manifest;
        const groundPercent = state.phase === "ground" && state.groundDurationMs ? Math.round(100 - ((state.groundRemainingMs || 0) / state.groundDurationMs) * 100) : 0;
        const holdPercent = state.phase === "hold" && state.holdDurationMs ? Math.round(100 - ((state.holdRemainingMs || 0) / state.holdDurationMs) * 100) : 0;
        const ringPercent = state.phase === "ground" ? groundPercent : state.phase === "hold" ? holdPercent : 0;

        return (
          <Marker key={`plane-${route.id}-${manifest ? manifest.id : "idle"}-${state.phase}`} position={point.position} icon={planeIcon(point.rotation, ringPercent, state.phase)} zIndexOffset={1000}>
            <Tooltip direction="top" offset={[0, -14]}>{plane.model} · {airportCode(point.from)} → {airportCode(point.to)}</Tooltip>
            <Popup autoPan={false}>
              <div className="planePopup">
                <img src={plane.image} alt={plane.model} />
                <strong>{plane.model}</strong>
                <span>{airportCode(point.from)} → {airportCode(point.to)}</span>
                {state.phase === "ground" ? <em>Odbavení na letišti: {groundPercent}%</em> : state.phase === "hold" ? <em>Kroužení u letiště: {holdPercent}%</em> : <em>Postup letu: {Math.round((state.progress || 0) * 100)}%</em>}
                {manifest && (
                  <div className="popupStats">
                    <span><b>{manifest.passengers}</b><small>cestující</small></span>
                    <span><b>{Math.round(manifest.loadFactor * 100)}%</b><small>obsazenost</small></span>
                    <span><b>{money(manifest.revenue)}</b><small>tržba</small></span>
                    <span><b>{money(manifest.costs)}</b><small>náklady</small></span>
                    <span><b>{money(manifest.profit - (state.event ? state.event.penalty || 0 : 0))}</b><small>čekaný zisk</small></span>
                  </div>
                )}
                {state.event && (
                  <div className={`eventInfo ${state.event.type}`}>
                    <b>{state.event.title}</b>
                    <span>{state.event.description}</span>
                  </div>
                )}
                <small>Stav letadla: {Math.round(planeCondition(plane))}%</small>
                <small>Celkem zisk linky: {money(route.totalProfit || 0)}</small>
              </div>
            </Popup>
          </Marker>
        );
      })}

      {visibleAirports.map(function (airport) {
        const selected = draftRoute.originId === airport.id || draftRoute.destinationId === airport.id;
        const markerColor = selected ? "#ff4d6d" : "#e11d48";
        return (
          <CircleMarker
            key={airport.id}
            center={[airport.lat, airport.lon]}
            radius={selected ? airportMarkerRadius(airport.type) + 2 : airportMarkerRadius(airport.type)}
            pathOptions={{ color: selected ? "#ffffff" : markerColor, fillColor: markerColor, fillOpacity: 0.95, weight: selected ? 3 : 2 }}
            eventHandlers={createMode ? { click: function () { onAirportPick(airport); } } : undefined}
          >
            <Tooltip>{airport.iata || airport.icao} · {airport.name}</Tooltip>
            <Popup autoPan={false}>
              <strong>{airport.name}</strong><br />
              {airport.iata || "bez IATA"} · {airport.icao}<br />
              {airportTypeLabels[airport.type] || airport.type}<br />
              {airport.country || "-"}
            </Popup>
          </CircleMarker>
        );
      })}
    </MapContainer>
  );
}
